import { Box, ColumnLayout, LineChart, SegmentedControl, SpaceBetween } from '@cloudscape-design/components';
import { dayToDate, formatCompact, formatDay, formatUsd, formatUsdAxis, summarizeTrend, yDomainFor, type TrendPoint } from './usageTrend';

export interface TrendDetail {
  tokensToday: number;
  costUsdToday: number;
  history: TrendPoint[];
}

/**
 * One chart, one metric at a time (tokens and dollars live on very different
 * scales). A time axis with formatted ticks, a y-axis pinned to start at zero,
 * and compact tick labels: the original categorical axis printed all 30 ISO
 * dates on top of each other, and an all-zero series got a degenerate y-axis.
 */
export function UsageTrendChart({
  detail,
  metric,
  onMetricChange,
}: {
  detail: TrendDetail;
  metric: 'tokens' | 'cost';
  onMetricChange: (m: 'tokens' | 'cost') => void;
}) {
  const { history } = detail;
  const summary = summarizeTrend(history);
  const isTokens = metric === 'tokens';
  const first = history[0];
  const last = history[history.length - 1];

  return (
    <SpaceBetween size="m">
      <ColumnLayout columns={3} variant="text-grid">
        <div>
          <Box variant="awsui-key-label">Last {history.length} days</Box>
          <Box variant="h3">
            {formatCompact(summary.totalTokens)} tokens · {formatUsd(summary.totalCostUsd)}
          </Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Today</Box>
          <Box variant="h3">
            {formatCompact(detail.tokensToday)} tokens · {formatUsd(detail.costUsdToday)}
          </Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Active days</Box>
          <Box variant="h3">
            {summary.activeDays} of {history.length}
          </Box>
        </div>
      </ColumnLayout>
      <SegmentedControl
        selectedId={metric}
        onChange={({ detail: d }) => onMetricChange(d.selectedId as 'tokens' | 'cost')}
        label="Metric"
        options={[
          { id: 'tokens', text: 'Tokens' },
          { id: 'cost', text: 'Cost ($)' },
        ]}
      />
      {summary.activeDays === 0 || !first || !last ? (
        <Box textAlign="center" color="text-body-secondary" padding="l">
          No usage recorded in this period.
        </Box>
      ) : (
        <LineChart
          series={[
            {
              title: isTokens ? 'Tokens' : 'Estimated cost',
              type: 'line',
              data: history.map((p) => ({ x: dayToDate(p.date), y: isTokens ? p.tokens : p.costUsd })),
              valueFormatter: (y) => (isTokens ? y.toLocaleString() : formatUsd(y)),
            },
          ]}
          xScaleType="time"
          xDomain={[dayToDate(first.date), dayToDate(last.date)]}
          yDomain={yDomainFor(isTokens ? summary.maxTokens : summary.maxCostUsd)}
          xTitle="Date (UTC)"
          yTitle={isTokens ? 'Tokens' : 'USD'}
          height={300}
          hideFilter
          hideLegend
          ariaLabel={isTokens ? 'Tokens per day' : 'Estimated cost per day'}
          empty={<Box color="text-body-secondary">No data.</Box>}
          i18nStrings={{
            xTickFormatter: (d) => formatDay(d),
            yTickFormatter: (y) => (isTokens ? formatCompact(y) : formatUsdAxis(y)),
          }}
        />
      )}
      <Box color="text-body-secondary" fontSize="body-s">
        Cost is an estimate at published Anthropic per-token rates, not a billing reconciliation.
      </Box>
    </SpaceBetween>
  );
}
